import React, { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { fetchClientForEdit, updateClient } from "../gestionCarteraApi";
import { formatDate } from "../gestionCarteraFormat";
import { COLORS, labelSx, ModalShell, SecondaryButton } from "../gestionCarteraUi";

const SELECT_OPTIONS = {
  documentType: ["CC", "CE", "NIT"],
  type: ["NATURAL", "JURIDICO"],
  occupationSector: ["AGRICULTURA", "GANADERIA", "MINERIA", "MANUFACTURA", "CONSTRUCCION", "COMERCIO", "SERVICIOS_FINANCIEROS", "ENERGIA", "TRANSPORTE_LOGISTICA", "OTROS"],
  occupationClient: ["INDEPENDIENTE", "PENSIONADO", "EMPLEADO"],
  segmentation: ["NEW_CLIENT", "FINISHED_CLIENT", "ACTIVE_CLIENT", "CONSULTED_CLIENT"],
};

const SEGMENTATION_LABELS = {
  NEW_CLIENT: "Cliente nuevo",
  FINISHED_CLIENT: "Cliente finalizado",
  ACTIVE_CLIENT: "Cliente activo",
  CONSULTED_CLIENT: "Cliente consultado",
};

/** Mismos campos y mismo PATCH /v1/clients/{uuid} que el antiguo módulo Clientes. */
const FIELDS = [
  { name: "name", label: "Nombre", section: "Identificación" },
  { name: "lastName", label: "Apellido", section: "Identificación" },
  { name: "documentType", label: "Tipo de documento", section: "Identificación" },
  { name: "documentNumber", label: "Número de documento", section: "Identificación" },
  { name: "documentExpeditionPlace", label: "Lugar expedición documento", section: "Identificación" },
  { name: "dateExpedition", label: "Fecha expedición documento", type: "date", section: "Identificación" },
  { name: "cellphone", label: "Número de contacto", section: "Contacto" },
  { name: "secondaryCellphone", label: "Celular secundario", section: "Contacto" },
  { name: "email", label: "Correo", section: "Contacto" },
  { name: "cityAddress", label: "Ciudad de residencia", section: "Contacto" },
  { name: "address", label: "Dirección", section: "Contacto", wide: true },
  { name: "placeOfBirth", label: "Lugar de nacimiento", section: "Personal" },
  { name: "ageRange", label: "Edad o rango de edad", section: "Personal" },
  { name: "gender", label: "Género", section: "Personal" },
  { name: "type", label: "Tipo cliente", section: "Personal" },
  { name: "occupation", label: "Ocupación", section: "Laboral" },
  { name: "occupationClient", label: "Tipo de ocupación", section: "Laboral" },
  { name: "occupationSector", label: "Sector de ocupación", section: "Laboral" },
  { name: "segmentation", label: "Segmentación", section: "Laboral" },
  { name: "estimatedIncome", label: "Ingresos estimados", section: "Laboral" },
  { name: "incomesExperian", label: "Ingresos Experian", section: "Laboral" },
  { name: "occupationDescription", label: "Descripción ocupación", section: "Laboral", wide: true },
];

const SECTIONS = ["Identificación", "Contacto", "Personal", "Laboral"];

/** El backend guarda la fecha de expedición como "DD/MM/YYYY"; el input usa "YYYY-MM-DD". */
const toInputDate = (value) => {
  const [day, month, year] = String(value || "").split("/");
  return year ? `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}` : "";
};

const toBackendDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year ? `${day}/${month}/${year}` : null;
};

const toForm = (client) =>
  FIELDS.reduce((form, field) => {
    const value = client?.[field.name];
    form[field.name] = field.type === "date" ? toInputDate(value) : value ?? "";
    return form;
  }, {});

const toRequest = (form) => ({
  ...form,
  documentType: form.documentType || "CC",
  dateExpedition: toBackendDate(form.dateExpedition),
  segmentation: form.segmentation || null,
});

const displayValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (field.name === "segmentation") return SEGMENTATION_LABELS[value] || value;
  if (field.type === "date") return formatDate(value);
  return value;
};

function FieldRow({ field, value, editing, saving, locked, onEdit, onChange, onSave, onCancel }) {
  const options = SELECT_OPTIONS[field.name];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.5,
        py: 0.75,
        minWidth: 0,
        borderBottom: `1px solid ${COLORS.border}`,
        gridColumn: field.wide ? { sm: "1 / -1" } : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={labelSx}>{field.label}</Typography>
        {editing ? (
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 0.5 }}
            type={field.type || "text"}
            select={Boolean(options)}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
          >
            {options?.map((option) => (
              <MenuItem key={option} value={option}>{SEGMENTATION_LABELS[option] || option}</MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography sx={{ mt: 0.25, fontWeight: 500, wordBreak: "break-word" }}>{displayValue(field, value)}</Typography>
        )}
      </Box>
      {editing ? (
        <Box sx={{ display: "flex", flex: "none" }}>
          <IconButton aria-label={`Guardar ${field.label}`} onClick={onSave} disabled={saving} sx={{ color: COLORS.primary, width: 32, height: 32 }}>
            {saving ? <CircularProgress size={18} /> : <CheckIcon />}
          </IconButton>
          <IconButton aria-label={`Cancelar ${field.label}`} onClick={onCancel} disabled={saving} sx={{ width: 32, height: 32 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <IconButton aria-label={`Editar ${field.label}`} onClick={onEdit} disabled={locked} sx={{ color: COLORS.primary, flex: "none", width: 32, height: 32, mt: 0.25 }}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

export default function EditarClienteModal({ open, clientUuid, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setForm(null);
    setEditing(null);
    setLoadError(false);
    fetchClientForEdit(clientUuid)
      .then((client) => setForm(toForm(client)))
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, clientUuid]);

  const startEdit = (field) => {
    setDraft(form[field.name]);
    setEditing(field.name);
  };

  const cancelEdit = () => {
    setForm((current) => ({ ...current, [editing]: draft }));
    setEditing(null);
  };

  const saveField = async () => {
    setSaving(true);
    try {
      await updateClient(clientUuid, toRequest(form));
      setSaving(false);
      setEditing(null);
      onSaved();
      Swal.fire({ icon: "success", title: "Dato actualizado", text: "Se guardó el cambio.", confirmButtonColor: COLORS.primary });
    } catch (error) {
      setSaving(false);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar el dato.", confirmButtonColor: COLORS.primary });
    }
  };

  const fullName = form ? [form.name, form.lastName].filter(Boolean).join(" ") : "";

  return (
    <ModalShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Datos completos del cliente"
      subtitle={fullName}
      maxWidth="md"
      actions={<SecondaryButton onClick={onClose} disabled={saving}>Cerrar</SecondaryButton>}
    >
      {loadError && (
        <Box role="alert" sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.primary }}>No pudimos cargar los datos del cliente</Typography>
          <SecondaryButton sx={{ mt: 2 }} onClick={load}>Reintentar</SecondaryButton>
        </Box>
      )}
      {!form && !loadError && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>}
      {form && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {SECTIONS.map((section) => (
            <Box key={section}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary, mb: 0.5 }}>{section}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 4 }}>
                {FIELDS.filter((field) => field.section === section).map((field) => (
                  <FieldRow
                    key={field.name}
                    field={field}
                    value={form[field.name]}
                    editing={editing === field.name}
                    saving={saving}
                    locked={Boolean(editing) && editing !== field.name}
                    onEdit={() => startEdit(field)}
                    onChange={(value) => setForm((current) => ({ ...current, [field.name]: value }))}
                    onSave={saveField}
                    onCancel={cancelEdit}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </ModalShell>
  );
}
